import {
  IFieldElementUnion,
  IFormSchemaV2,
  FormSchema,
  StaticTypes,
  isInputElement,
  isRepeatableElement,
  isStaticElement,
} from "@graffer-inc/form-schema";
import {
  Diff,
  diff,
  DiffArray,
  DiffDeleted,
  DiffEdit,
  DiffNew,
} from "deep-diff";
import { isNumber } from "lodash";

const kindDiffArray = "A";
const kindDiffNew = "N";
const kindDiffDeleted = "D";
const kindDiffEdit = "E";
export class CloudgovFormSchemaComparer {
  static checkOnlySafeDiff(
    before: IFormSchemaV2,
    after: IFormSchemaV2,
  ): string[] {
    // NOTE: https://graffer.atlassian.net/browse/PRODUCTS-12218
    const beforeSchemas = FormSchema.fromJSON(before);
    const afterSchemas = FormSchema.fromJSON(after);

    const diffs = diff(beforeSchemas, afterSchemas);
    // for debugging
    // console.log(JSON.stringify(diffs, null, 2));

    // 差分がない場合は更新しても問題が起きないので問題なしと扱う
    if (!diffs || diffs.length === 0) {
      return [];
    }

    const result = new Array<string>();
    diffs.forEach((d) => {
      const errMsg = this.onlySafeDiff(d, before, after);
      if (errMsg) {
        result.push(errMsg);
      }
    });

    return result;
  }

  static checkPdfOnlyDiff(
    before: IFormSchemaV2,
    after: IFormSchemaV2,
  ): string[] {
    // NOTE: https://graffer.atlassian.net/browse/PRODUCTS-12218
    // - https://github.com/graffer-inc/cloud-gov/pull/15514
    // - https://github.com/graffer-inc/cloud-gov/pull/15526
    const beforeSchemas = FormSchema.fromJSON(before);
    const afterSchemas = FormSchema.fromJSON(after);

    const diffs = diff(beforeSchemas, afterSchemas);
    // for debugging
    // console.log(JSON.stringify(diffs, null, 2));

    // 差分がない場合は更新しても問題が起きないので問題なしと扱う
    if (!diffs || diffs.length === 0) {
      return [];
    }

    const result = new Array<string>();
    diffs.forEach((d) => {
      const errMsg = this.onlyPdfDiff(d);
      if (errMsg) {
        result.push(errMsg);
      }
    });
    // for debugging
    // console.log(result);

    return result;
  }

  private static onlySafeDiff(
    diff: Diff<IFormSchemaV2, IFormSchemaV2>,
    before: IFormSchemaV2,
    after: IFormSchemaV2,
  ): string | undefined {
    // 現状、以下の要素のみ更新を許可する
    // - 汎用項目
    //   - 入力項目（InputElement, RepeatableElement）の説明文
    //   - 入力項目（InputElement, RepeatableElement）の説明文のisMarkdown属性
    //   - 入力項目（InputElement）のプレースホルダー
    // - 個別項目
    //   - ファイル項目の最大サイズの編集
    //   - 説明文項目の説明文の編集
    //   - 説明文項目のisMarkdown属性の編集
    if (!diff.path) {
      return;
    }

    const paths = diff.path.join(".");
    // validate diff path
    if (paths.startsWith("conditionalRuleSet")) {
      return "conditional_rule_set_not_allowed";
    }
    if (paths.startsWith("formulaSet")) {
      return "formula_set_not_allowed";
    }

    // validate diff kind
    switch (diff.kind) {
      case kindDiffArray:
        return "array_diff_not_allowed";
      case kindDiffDeleted:
        return this.validateKindDiffDeleted(diff, paths, before);
      case kindDiffNew:
        return this.validateKindDiffNew(diff, paths, after); // DiffNewの場合は変更後schemaから要素を探す必要があるためafterを渡す
      case kindDiffEdit:
        return this.validateKindDiffEdit(diff, paths, before);
      default:
        throw new Error(`invalid diff kind`);
    }
  }

  private static validateKindDiffDeleted(
    diff: DiffDeleted<IFormSchemaV2>,
    paths: string,
    before: IFormSchemaV2,
  ): string | undefined {
    const diffElement = this.findFieldElementByPath(before, diff);
    if (!diffElement) {
      return "invalid_element_deleted";
    }

    // NOTE: isMarkdownのハンドリングについて
    // フロントから送られてきたschemaをcheckOnlySafeDiff()の冒頭でFormSchemaをコンストラクタを通して再生成しているが、
    // その際に説明文項目以外の項目のdescription.isMarkdownは「プロパティ自体が存在しない」という状態が保たれる。
    // そのためDiffNew, DiffDeleteとして扱われる必要がある場合が存在しており、ここでハンドリングする。
    // 一方で、説明文項目のisMarkdownはプロパティ自体が存在しない場合に「isMarkdown:undefined」として初期化される。
    // したがって、説明文項目のisMarkdownはDiffEditとして差分にのるため、ここではハンドリング不要。

    if (isInputElement(diffElement)) {
      if (paths.endsWith("description.isMarkdown")) {
        return;
      }
    }

    if (isRepeatableElement(diffElement)) {
      if (paths.endsWith("description.isMarkdown")) {
        return;
      }
    }

    return "invalid_element_deleted";
  }

  private static validateKindDiffNew(
    diff: DiffNew<IFormSchemaV2>,
    paths: string,
    after: IFormSchemaV2,
  ): string | undefined {
    const diffElement = this.findFieldElementByPath(after, diff);
    if (!diffElement) {
      return "invalid_element_added";
    }

    // NOTE: isMarkdownのハンドリングについて
    // フロントから送られてきたschemaをcheckOnlySafeDiff()の冒頭でFormSchemaをコンストラクタを通して再生成しているが、
    // その際に説明文項目以外の項目のdescription.isMarkdownは「プロパティ自体が存在しない」という状態が保たれる。
    // そのためDiffNew, DiffDeleteとして扱われる必要がある場合が存在しており、ここでハンドリングする。
    // 一方で、説明文項目のisMarkdownはプロパティ自体が存在しない場合に「isMarkdown:undefined」として初期化される。
    // したがって、説明文項目のisMarkdownはDiffEditとして差分にのるため、ここではハンドリング不要。

    if (isInputElement(diffElement)) {
      if (paths.endsWith("description.isMarkdown")) {
        return;
      }
    }

    if (isRepeatableElement(diffElement)) {
      if (paths.endsWith("description.isMarkdown")) {
        return;
      }
    }

    return "invalid_element_added";
  }

  private static validateKindDiffEdit(
    diff: DiffEdit<IFormSchemaV2, IFormSchemaV2>,
    paths: string,
    before: IFormSchemaV2,
  ): string | undefined {
    const diffElement = this.findFieldElementByPath(before, diff);
    if (!diffElement) {
      return "invalid_element_edited";
    }

    if (isInputElement(diffElement)) {
      // common fields
      if (paths.endsWith("description.text")) {
        return;
      }

      if (paths.endsWith("description.isMarkdown")) {
        return;
      }

      if (paths.endsWith("placeholder")) {
        return;
      }

      if (diffElement.inputType === "file") {
        if (paths.endsWith("maxSize")) {
          return;
        }
      }
    }

    if (isRepeatableElement(diffElement)) {
      if (paths.endsWith("description.text")) {
        return;
      }

      if (paths.endsWith("description.isMarkdown")) {
        return;
      }
    }

    if (isStaticElement(diffElement)) {
      if (diffElement.staticType === StaticTypes.Description) {
        if (paths.endsWith("text")) {
          return;
        }

        if (paths.endsWith("isMarkdown")) {
          return;
        }
      }
    }

    return "invalid_element_edited";
  }

  // DiffEdit の path 情報を元に formSchema を探索し、IFieldElementUnion を返す。
  // path と返す IFieldElementUnion の例は以下の通り。
  //
  // ['root', 1,'children', 0, 'placeholder']
  //   => root[1].children[0]
  // ['root', 1,'children', 0, 'baseUnit', 0, 'description', 'text' ]
  //   => root[1].children[0].baseUnit[0]
  //
  // IFormSchemaV2 の構造に基づき を探索し IFieldElementUnion が存在する children 配下と baseUnit 配下を探索対象としている。
  // よって、path の対象が IFieldElementUnion 以外（ ISectionElement や IConditionalRuleSet など ）の場合は undefined を返す。
  // 後続の処理で他の要素が必要になる場合は、本関数を拡張する必要がある。
  private static findFieldElementByPath(
    formSchema: IFormSchemaV2,
    diff: Diff<IFormSchemaV2, IFormSchemaV2>,
  ): IFieldElementUnion | undefined {
    if (!diff.path) {
      return;
    }

    const pathItems = [...diff.path];

    if (pathItems.shift() !== "root") {
      return;
    }

    const sectionIndex = pathItems.shift();
    if (!isNumber(sectionIndex)) {
      return;
    }
    const section = formSchema.root[sectionIndex];

    if (pathItems.shift() !== "children") {
      return;
    }
    const childIndex = pathItems.shift();
    if (!isNumber(childIndex)) {
      return;
    }
    const child = section.children[childIndex];

    if (pathItems.shift() === "baseUnit") {
      if (!isRepeatableElement(child)) {
        return;
      }

      const baseUnitIndex = pathItems.shift();
      if (!isNumber(baseUnitIndex)) {
        return;
      }

      return child.baseUnit[baseUnitIndex];
    } else {
      return child;
    }
  }

  private static onlyPdfDiff(
    diff: Diff<IFormSchemaV2, IFormSchemaV2>,
  ): string | undefined {
    if (!diff.path) {
      return;
    }
    if (!diff.path.some((p) => p === "root")) {
      return "only_root_allowed";
    }
    if (!diff.path.some((p) => p === "children")) {
      return "only_children_allowed";
    }
    // まずはPDF設定が変更された場合のみ手続きモデルの更新を受け付ける
    if (diff.path.some((p) => p === "pdfAdapterRule")) {
      return;
    }
    if (!diff.path.some((p) => p === "schemaAtIndex")) {
      return "only_pdf_allowed";
    }

    if (diff.kind === "E") {
      if (!!diff.rhs) {
        if (Array.isArray(diff.rhs)) {
          if (
            diff.rhs.some((d) => {
              return Object.keys(d).some((k) => k !== "pdfAdapterRule");
            })
          ) {
            return "only_pdfAdapterRule_in_schemaAtIndex_E_rhs[]";
          }
        } else {
          if (Object.keys(diff.rhs).some((k) => k !== "pdfAdapterRule")) {
            return "only_pdfAdapterRule_in_schemaAtIndex_E_rhs";
          }
        }
      }
      if (!!diff.lhs) {
        if (Array.isArray(diff.lhs)) {
          if (
            diff.lhs.some((d) => {
              return Object.keys(d).some((k) => k !== "pdfAdapterRule");
            })
          ) {
            return "only_pdfAdapterRule_in_schemaAtIndex_E_lhs[]";
          }
        } else {
          if (Object.keys(diff.lhs).some((k) => k !== "pdfAdapterRule")) {
            return "only_pdfAdapterRule_in_schemaAtIndex_E_lhs";
          }
        }
      }
      return;
    }
    // when schemaAtIndex is in the path, key of rhs or lhs must be pdfAdapterRule
    if (diff.kind !== "A") {
      return "only_schemaAtIndex_index_allowed";
    }

    // TODO refactor; structure of schemaAtIndex is complicated
    const diffArray = diff as DiffArray<Array<{}> | {}, Array<{}> | {}>;
    switch (diffArray.item.kind) {
      case "N":
        if (!diffArray.item.rhs) {
          // when null or undefined element is added
          return;
        }
        if (Array.isArray(diffArray.item.rhs)) {
          const diffNew = diffArray.item as DiffNew<Array<Object>>;
          if (
            diffNew.rhs.some((d) => {
              return !!d && Object.keys(d).some((k) => k !== "pdfAdapterRule");
            })
          ) {
            return "only_pdf_allowed_in_new_schemaAtIndex";
          }
          return;
        }
        const diffNew = diffArray.item as DiffNew<Object>;
        if (Object.keys(diffNew.rhs).some((k) => k !== "pdfAdapterRule")) {
          return "only_pdf_allowed_in_new_schemaAtIndex";
        }
        return;
      case "D":
        if (!diffArray.item.lhs) {
          // when null or undefined element is deleted
          return;
        }
        if (Array.isArray(diffArray.item.lhs)) {
          const diffDeleted = diffArray.item as DiffDeleted<Array<Object>>;
          if (
            diffDeleted.lhs.some((d) => {
              return !!d && Object.keys(d).some((k) => k !== "pdfAdapterRule");
            })
          ) {
            return "only_pdf_allowed_in_deleted_schemaAtIndex";
          }
          return;
        }
        const diffDeleted = diffArray.item as DiffDeleted<Object>;
        if (Object.keys(diffDeleted.lhs).some((k) => k !== "pdfAdapterRule")) {
          return "only_pdf_allowed_in_deleted_schemaAtIndex";
        }
        return;
      default:
        return "only_pdf_allowed_in_schemaAtIndex";
    }
  }
}
